/*
 * WHICH NAMES IN A VERSE LEAD SOMEWHERE, AND WHAT THEY ARE SPELLED LIKE.
 *
 * Both surfaces need this and until now only one could reach it. `nameSpans` lived in `core`, which
 * the app imports and `pipeline` does not, so every name in every verse was a door in the app and
 * plain text on all 2,688 static pages — 43,858 passages of scripture with 1,099 figures in them
 * and not one of the names linked anywhere.
 *
 * CLAUDE.md already says where a rule two surfaces must agree on belongs, and it is here. This is
 * the third time: `sectionKey` and `VerseNote` were moved for the same reason, both after the two
 * surfaces had quietly disagreed. Moving it also settles the older question of whether `pipeline`
 * should depend on `core` — for this rule it does not have to, because the rule was never about
 * the app.
 *
 * `core/verses.ts` re-exports these so nothing that imports them today has to change.
 */

/** A name, and every spelling the texts give it. */
export interface NameForms {
  id: string;
  forms: string[];
}

/** Where a name sits in a verse. Half-open: [start, end). */
export interface NameSpan {
  start: number;
  end: number;
  id: string;
}

/** Just enough of a figure to get its spellings. Structural, so neither surface's type leaks here. */
export interface NamedFigure {
  readonly id: string;
  readonly name: string;
  /** Spellings keyed by the text they come from — `{ kjv: ['Abraham', 'Abram'], quran: [...] }`. */
  readonly forms?: Readonly<Record<string, readonly string[]>>;
}

/** Shorter than this matches inside half the words in scripture. */
const SHORTEST_FORM = 3;

/**
 * The lookup table `mentions` indexes into: one entry per figure, in the order the bundle lists them.
 *
 * IT IS A DERIVATION AND IT WAS WRITTEN INLINE IN `App.tsx`, which is why it is here. The label and
 * the spellings are two different fields and a table built from either alone is wrong in a way that
 * looks fine: forms without the name misses every figure whose label is the only spelling it has,
 * and the name without forms tells a reader the Quran does not mention Moses because it writes Musa.
 *
 * The index is the POSITION, so this must stay a straight map over the same array the chunk's
 * `mentions` were numbered against. Filtering it here would silently shift every name in the library
 * by however many were dropped.
 */
export function nameFormsOf(figures: readonly NamedFigure[]): NameForms[] {
  return figures.map(figure => ({
    id: figure.id,
    forms: [figure.name, ...Object.values(figure.forms ?? {}).flat()],
  }));
}

/**
 * TRAVELLING BY WORD.
 *
 * The point of a map like this is that you can move through it, and reading a verse should
 * not be a dead end: every name inside the text is a door to that name's own verses. So the
 * English is scanned for names the map knows, and the caller turns each span into a link.
 *
 * Spellings count, not just labels — someone reading "Musa" in the Quran is reading about
 * Moses, and a name-only scan would tell them the scriptures do not mention him.
 *
 * Returned as SPANS rather than as marked-up text, because a phone app does not build the
 * same thing out of them that a web page does, and because a function that returns ranges
 * can be tested and one that returns a string of HTML mostly cannot.
 */
export function nameSpans(text: string, names: readonly NameForms[]): NameSpan[] {
  const src = String(text ?? '');
  if (!src || !names?.length) return [];
  /* Lowercased once. Doing it inside the search loop is a fresh copy of the whole verse
     per occurrence of every spelling of every name in it. */
  const hay = src.toLowerCase();

  const found: (NameSpan & { len: number })[] = [];
  for (const n of names) {
    for (const raw of n.forms ?? []) {
      const form = String(raw ?? '');
      if (form.length < SHORTEST_FORM) continue;
      const needle = form.toLowerCase();
      let from = 0, at: number;
      while ((at = hay.indexOf(needle, from)) !== -1) {
        from = at + needle.length;
        /* A name inside a longer word is not that name: "Israelites" is not "Israel"
           followed by "ites". Letters only — a possessive apostrophe still counts. */
        const before = src[at - 1], after = src[at + form.length];
        if (before && /[A-Za-z]/.test(before)) continue;
        if (after && /[A-Za-z]/.test(after)) continue;
        found.push({ start: at, end: at + form.length, id: n.id, len: form.length });
      }
    }
  }
  if (!found.length) return [];

  /* Earliest first, and at the same place the longest wins, so "children of Israel" is one
     name rather than "Israel" with three stray words in front of it. */
  found.sort((a, b) => a.start - b.start || b.len - a.len);

  const out: NameSpan[] = [];
  let cursor = 0;
  for (const s of found) {
    if (s.start < cursor) continue;              // already inside a name
    out.push({ start: s.start, end: s.end, id: s.id });
    cursor = s.end;
  }
  return out;
}
